import {Search} from '@mui/icons-material';
import {Box, OutlinedInput, Typography} from '@mui/material';
import {FC, useEffect, useMemo, useRef, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {useGetRolesQuery} from 'src/api';
import {searchInputSx, toolbarActionsSx} from 'src/components/tables/tableToolbar.sx';
import {TablePagination} from 'src/components/tables/TablePagination';
import {TableRowCounter} from 'src/components/tables/TableRowCounter';
import {cardSx, emptySx, listFooterSx, listScrollSx, sectionHintSx} from 'src/pages/AccessControl/AccessControl.sx';
import {columnHeadSx, listHeaderRowSx} from '../iam.sx';
import {ROLE_KINDS, RoleKind, roleKind} from '../roleKind';
import {ToolbarSearchSelect} from 'src/components/common/ToolbarSearchSelect';
import {RoleRow} from '../RoleRow';
import {useBusinessId} from 'src/app/hooks';

/**
 * The roles this business can hand out.
 *
 * One list. Every role belongs to at least one business, so there is no second class of role to
 * separate out and no heading needed to explain the difference — the previous split into "yours" and
 * "shared" was describing a distinction that no longer exists.
 */
const KIND_LABEL: Record<RoleKind, string> = {
    builtIn: 'accessControlBuiltInRole',
    custom: 'accessControlCustomRole',
};

export const RolesSection: FC = () => {
    const intl = useIntl();
    // Roles belong to a business, so the list follows the one being worked in.
    const businessId = useBusinessId();
    const {data: roles} = useGetRolesQuery(businessId);
    const [keyword, setKeyword] = useState('');
    const [kind, setKind] = useState('');
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(25);

    const filtered = useMemo(() => {
        const needle = keyword.trim().toLowerCase();
        return (roles ?? []).filter(
            (role) => (!kind || roleKind(role) === kind) && (
                !needle ||
                role.name.toLowerCase().includes(needle) ||
                // Still matched, though no longer shown: somebody who knows a seeded role by its code
                // - from a log line or a migration - should still find it by typing that.
                role.code.toLowerCase().includes(needle))
        );
    }, [roles, keyword, kind]);

    /**
     * Paged here rather than by the server, which returns them all in one list.
     *
     * Roles are tens, not thousands - a business writes a handful on top of the seeded ones - so
     * fetching the lot is the cheaper request and the page footer is about reading rather than about
     * transfer. It also means searching narrows the pages themselves instead of paging through
     * matches, which is what somebody typing a name expects.
     */
    const totalPages = Math.max(1, Math.ceil(filtered.length / size));
    const shown = useMemo(
        () => filtered.slice(page * size, page * size + size),
        [filtered, page, size]
    );

    // A narrower search can leave the current page past the end, and a list that answers an empty
    // page to a search with matches reads as no matches at all.
    useEffect(() => {
        setPage(0);
    }, [keyword, kind, size]);

    // A new page starts at its first row, as in every other list - the scroller is replaced in place.
    const scrollRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        scrollRef.current?.scrollTo({top: 0});
    }, [page]);

    return (
        <Box>
            <Typography sx={sectionHintSx}>
                <FormattedMessage
                    id="accessControlRolesHint"
                    defaultMessage="A role is a named set of permissions. Give someone a role and they can do exactly what it lists — nothing is implied by their job title."
                />
            </Typography>

            <Box sx={{...toolbarActionsSx, mb: 2.5, gap: 1.5, flexWrap: 'wrap'}}>
                <OutlinedInput
                    sx={searchInputSx}
                    color="secondary"
                    type="text"
                    name="search"
                    value={keyword}
                    onChange={(event) => setKeyword(event.target.value)}
                    placeholder={intl.formatMessage({id: 'search'})}
                    startAdornment={<Search/>}
                />
                {/* Reads the kind the same way the column does, through roleKind - which is why that
                    lives in a file of its own rather than as a ternary in each place. */}
                <ToolbarSearchSelect
                    value={kind}
                    onChange={setKind}
                    width={200}
                    options={ROLE_KINDS.map((value) => ({
                        value,
                        label: intl.formatMessage({id: KIND_LABEL[value], defaultMessage: value}),
                    }))}
                    emptyLabel={intl.formatMessage({
                        id: 'accessControlAllRoleKinds',
                        defaultMessage: 'All types',
                    })}
                />
            </Box>

            {/* Built exactly like the people beside it - same card, same bounded scroller, same
                footer - because these two are tabs of one screen and anything that differs between
                them reads as the page rearranging itself when you switch. */}
            <Box sx={cardSx}>
                <Box ref={scrollRef} sx={listScrollSx}>
                    {/* Inside the scroller and stuck to its top, so it is measured against the same
                        width as the rows whether a scrollbar appears or not. */}
                    <Box sx={listHeaderRowSx}>
                        {/* Stands in for the shield badge on every row below. */}
                        <Box sx={{width: 36, flexShrink: 0}}/>
                        {/* Fixed, like Person on the people tab, so the kind beside it sits next to the
                            name instead of being pushed to the far side by a column that grows. */}
                        <Typography sx={{...columnHeadSx, width: 300, flex: 'none'}}>
                            <FormattedMessage id="accessControlColumnRole" defaultMessage="Role"/>
                        </Typography>
                        <Typography sx={{...columnHeadSx, width: 120, flex: 'none'}}>
                            <FormattedMessage id="accessControlColumnRoleKind" defaultMessage="Type"/>
                        </Typography>
                        {/* What used to grow. The counts stay against the right edge, where the same
                            counts sit on the people tab. */}
                        <Box sx={{flex: 1}}/>
                        <Typography sx={{...columnHeadSx, width: 88, flex: 'none', mr: 3}}>
                            <FormattedMessage
                                id="accessControlColumnPermissions"
                                defaultMessage="Permissions"
                            />
                        </Typography>
                        <Typography sx={{...columnHeadSx, width: 88, flex: 'none', mr: 3}}>
                            <FormattedMessage id="accessControlColumnPeople" defaultMessage="People"/>
                        </Typography>
                        {/* Keeps the heading aligned with the rows, which end in a chevron. */}
                        <Box sx={{width: 24}}/>
                    </Box>

                    {shown.length === 0 ? (
                        <Box sx={emptySx}>
                            <Typography sx={{fontWeight: 600, mb: 0.5}}>
                                <FormattedMessage
                                    id="accessControlNoRoles"
                                    defaultMessage="No roles found"
                                />
                            </Typography>
                        </Box>
                    ) : (
                        shown.map((role) => <RoleRow key={role.id} role={role}/>)
                    )}
                </Box>
                <Box sx={listFooterSx}>
                    <TableRowCounter
                        defaultValue={size}
                        handleChangeRowsNumber={(event) => {
                            setSize(Number(event.target.value));
                            setPage(0);
                        }}
                    />
                    <TablePagination
                        totalPages={totalPages}
                        page={page}
                        handleChangePage={setPage}
                    />
                </Box>
            </Box>
        </Box>
    );
};
